import { type ChangeEvent, type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncFn, useDebounce } from 'react-use';
import { type AsyncState } from 'react-use/lib/useAsync';
import styled from 'styled-components';

import { Text, Textarea } from '@soluto-private/mx-asurion-ui-react';

import {
  useAnalytics,
  useInViewAnalytics,
} from '@service-initiation/shared-data-access-analytics';
import {
  type Suggestion,
  getPlacePredictions,
  placeIdToAddress,
} from '@service-initiation/shared-data-access-google';
import {
  formatZip,
  validateZipCode,
} from '@service-initiation/shared-utils-validators';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import {
  DeliveryMethod,
  type GooglePlacesApiExtractedAddress,
  useDeliveryMethodAtom,
  useRemoteTechAtom,
} from '@service-initiation/tech-care-data-access-store';
import { type ErrorType } from '@service-initiation/tech-care-data-access-tech-care-services';

import houseAndVangImg from '../../assets/house-and-van-img.png';
import { PredictiveManualAddressInput } from '../../components';
import { extractedAddressToFullString } from '../../utils';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
  gap: 24px;
`;

const PageBreak = styled.hr`
  border-top: 1px solid rgb(230, 230, 235);
  margin: 48px 0px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: inherit;
  gap: 8px;
`;

const AnywhereRepairValueSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
  gap: 10px;
  margin-bottom: 80px;
`;

const AnywhereRepairImage = styled.img`
  max-height: 218px;
  max-width: 500px;
  width: 85%;
  align-self: center;
`;

const TEXT = TranslationEntries.Repairs.ComeToMe;
const NO_RESULTS = 'INVALID';

export type RemoteTechTabProps = {
  validationError: ErrorType | undefined;
};

export const RemoteTechTab: FC<RemoteTechTabProps> = ({ validationError }) => {
  const [instructions, setInstructions] = useState<string>('');
  const [, setDeliveryMethod] = useDeliveryMethodAtom();
  const [remoteTech, setRemoteTech] = useRemoteTechAtom();
  const [inputText, setInputText] = useState<string>(
    extractedAddressToFullString(remoteTech?.meetingAddress)
  );
  const [predictions, setPredictions] = useState<Suggestion[]>();
  const [manualInputFields, setManualInputFields] = useState<
    GooglePlacesApiExtractedAddress | undefined
  >(remoteTech?.meetingAddress);
  const { dispatchClickEvent } = useAnalytics();

  const { ref: elementRef } = useInViewAnalytics('RemoteTechTab');

  const updatePredictions = useCallback(
    async (newInput: string) => {
      if (newInput) {
        const res = await getPlacePredictions(newInput, {
          types: ['address'],
        });
        if (res?.predictions.length === 0 || res === undefined) {
          setPredictions([
            { header: 'No Results for that location', id: NO_RESULTS },
          ] as Suggestion[]);
          dispatchClickEvent('RemoteTechInvalidInput', {
            Cta: 'Done',
            addressInput: newInput,
          });
        } else {
          const formattedPreds = res?.predictions
            .filter((pred) => pred.description.split(',').length > 2)
            .map((prediction) => {
              return {
                header: prediction.structured_formatting.main_text,
                id: prediction.place_id,
                subHeader: prediction.structured_formatting.secondary_text,
                value: prediction.description,
              } as Suggestion;
            });
          setPredictions(formattedPreds);
        }
      }
    },
    [dispatchClickEvent]
  );

  useDebounce(
    async () => {
      await updatePredictions(inputText);
    },
    375,
    [updatePredictions, inputText]
  );

  const handleTextChange = useCallback(async (input: string) => {
    setInputText(input);
  }, []);

  const checkIfUpdateNeeded = useCallback(
    (
      address: GooglePlacesApiExtractedAddress,
      areAllManualInputsFilled: boolean
    ) => {
      if (address || areAllManualInputsFilled) {
        setDeliveryMethod(DeliveryMethod.ComeToMe);
        setRemoteTech({
          ...remoteTech,
          meetingAddress: address,
          specialInstructions: instructions,
        });
      }
    },
    [instructions, remoteTech, setDeliveryMethod, setRemoteTech]
  );

  const validateInputFields = useCallback(
    (address: GooglePlacesApiExtractedAddress) => {
      let areAllInputFieldsFilled = false;
      if (
        address?.streetAddress !== undefined &&
        address?.city !== undefined &&
        address?.state !== undefined &&
        address?.zip !== undefined &&
        validateZipCode(address.zip)
      ) {
        areAllInputFieldsFilled = true;
      }
      if (
        address?.streetAddress === '' ||
        address?.city === '' ||
        address?.state === '' ||
        address?.zip === ''
      ) {
        areAllInputFieldsFilled = false;
      }
      checkIfUpdateNeeded(address, areAllInputFieldsFilled);
    },
    [checkIfUpdateNeeded]
  );

  const handleManualFieldChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      const name = ev.target.name;
      const value = ev.target.value;

      const formattedValue = name === 'zip' ? formatZip(value) : value;
      const newManualInputFields = {
        ...manualInputFields,
        [name as keyof GooglePlacesApiExtractedAddress]: formattedValue,
      };

      setManualInputFields(newManualInputFields);
      validateInputFields(newManualInputFields);
    },
    [manualInputFields, validateInputFields]
  );

  const [state, onLocationSelect] = useAsyncFn(async (place: Suggestion) => {
    place.id !== NO_RESULTS && setInputText(place.value);
    if (place.id !== NO_RESULTS) {
      const address = await placeIdToAddress(place.id);
      if (address !== undefined) {
        setManualInputFields(address);
        validateInputFields(address);
      }
    }
  }, []);

  const { t } = useTranslation();
  const loadingState = {
    loading: (state as AsyncState<boolean>).loading,
  } as AsyncState<boolean>;

  return (
    <PageContainer ref={elementRef}>
      <InputSection>
        <TextContainer>
          <Text size={3}>{t(TEXT.AddressSection.Header)}</Text>
          <Text weight="feather">{t(TEXT.AddressSection.Subheader)}</Text>
        </TextContainer>
        <div>
          <PredictiveManualAddressInput
            handleManualFieldChange={handleManualFieldChange}
            handleTextChange={handleTextChange}
            inputText={inputText}
            manualInputFields={manualInputFields}
            onLocationSelect={onLocationSelect}
            predictions={predictions}
            state={loadingState}
            validationError={validationError}
          />
        </div>
        <Textarea
          data-testid="instructions-field"
          helperText={t(TEXT.AddressSection.InstructionsHelper)}
          label={t(TEXT.AddressSection.InstructionsField)}
          onChange={(e) => setInstructions(e.target.value)}
          value={instructions}
        />
      </InputSection>
      <PageBreak />
      <AnywhereRepairValueSection>
        <TextContainer>
          <Text size={2} weight={'heavy'}>
            {t(TEXT.ImageSection.Header)}
          </Text>
          <Text weight="feather">{t(TEXT.ImageSection.Subheader)}</Text>
        </TextContainer>
        <AnywhereRepairImage alt="house and van" src={houseAndVangImg} />
      </AnywhereRepairValueSection>
    </PageContainer>
  );
};
